<script lang="ts" setup>
import { useBuilderStore } from '@/store/builder'
import {
  RTEControlBold,
  RTEControlColorPicker,
  RTEControlFontFamily,
  RTEControlFontSize,
  RTEControlItalic,
  RTEControlLineHeight,
  RTEControlList,
  RTEControlResetStyles,
  RTEControlStrike,
  RTEControlTextAlignment,
  RTEControlTextBlocks,
  RTEControlUnderline,
  RTEControlCustomSelect,
} from '@gohighlevel/ghl-text-editor'
import { UIDivider } from '@gohighlevel/ghl-ui'
import { useAppStore } from '@/store/app'
import {
  useCustomValues,
  CustomSelectTrigger,
  DEFAULT_OPEN_SANS_FONT_FOR_VERSION_2,
  FontFamilyDropDownControl,
} from '@gohighlevel/ghl-proposals-common'
import RTELinkWrapper from './RTELinkWrapper.vue'
import { computed } from 'vue'
import { union } from 'lodash'
import { useDocument } from '@/composition'

const store = useBuilderStore()
const appStore = useAppStore()
const { documentVersion } = useDocument()
const { customMenuOptions, staticCustomValues } = useCustomValues()

const fallbackFont = computed(() => {
  if (documentVersion.value > 1) {
    return DEFAULT_OPEN_SANS_FONT_FOR_VERSION_2
  }
  return 'sans-serif'
})

// Handle newly loaded fonts from the FontFamilyDropdown component
const onFontsLoaded = (newFonts: string[]) => {
  // Update the store's loadedFonts array with the newly loaded fonts
  store.setLoadedFonts(union(store.loadedFonts, newFonts))
}
</script>

<template>
  <div
    :key="'editor-toolbar-' + store.activeElementId"
    @mousedown.prevent
    id="editor-toolbar"
    :class="`flex justify-center items-center space-x-2 ${
      !store.isDocumentEditable ? 'pointer-events-none opacity-50' : ''
    }`"
  >
    <template v-if="store.editor">
      <div class="font-family-dropdown">
        <RTEControlFontFamily
          v-if="documentVersion <= 1"
          :fallback-font="'sans-serif'"
          :editor="store.editor"
          :language="appStore.locale"
        />
        <FontFamilyDropDownControl
          :show="documentVersion > 1"
          :editor="store.editor"
          :fallback-font="fallbackFont"
          :loaded-fonts="store.loadedFonts"
          @fonts-loaded-from-control="onFontsLoaded"
          class="p-1"
          :fonts-to-load="store.fontsToLoad"
        />
      </div>
      <RTEControlFontSize
        :editor="store.editor"
        :language="appStore.locale"
        :fallbackFontSize="16"
        v-if="documentVersion <= 1"
      />
      <RTEControlFontSize
        :editor="store.editor"
        :language="appStore.locale"
        :fallbackFontSize="16"
        v-if="documentVersion > 1"
        :font-sizes="[
          10, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 34, 36, 48, 60,
        ]"
      />
      <div>
        <RTEControlTextBlocks
          :editor="store.editor"
          :language="appStore.locale"
          :options="[
            'Heading1',
            'Heading2',
            'Heading3',
            'Heading4',
            'Heading5',
            'Paragraph',
          ]"
        />
      </div>

      <UIDivider :vertical="true" class="divider" />
      <RTEControlBold :editor="store.editor" :language="appStore.locale" />
      <RTEControlItalic :editor="store.editor" :language="appStore.locale" />
      <RTEControlUnderline :editor="store.editor" :language="appStore.locale" />
      <RTEControlStrike :editor="store.editor" :language="appStore.locale" />
      <UIDivider :vertical="true" class="divider" />
      <RTELinkWrapper :editor="store.editor" :language="appStore.locale" />
      <UIDivider :vertical="true" class="divider" />
      <RTEControlTextAlignment
        :align="'justify'"
        :editor="store.editor"
        :language="appStore.locale"
      />
      <RTEControlTextAlignment
        :align="'left'"
        :editor="store.editor"
        :language="appStore.locale"
      />
      <RTEControlTextAlignment
        :align="'center'"
        :editor="store.editor"
        :language="appStore.locale"
      />
      <RTEControlTextAlignment
        :align="'right'"
        :editor="store.editor"
        :language="appStore.locale"
      />
      <UIDivider :vertical="true" class="divider" />
      <RTEControlLineHeight
        :editor="store.editor"
        :language="appStore.locale"
        fallback-line-height="1.5"
        :line-height-options="[
          { value: '0.5', label: '0.5' },
          { value: '0.75', label: '0.75' },
          { value: '1', label: '1' },
          { value: '1.15', label: '1.15' },
          { value: '1.5', label: '1.5' },
          { value: '2', label: '2' },
        ]"
      />
      <UIDivider :vertical="true" class="divider" />
      <RTEControlList
        :editor="store.editor"
        listType="bulletList"
        :language="appStore.locale"
      />
      <RTEControlColorPicker
        :editor="store.editor"
        :language="appStore.locale"
      />
      <RTEControlResetStyles
        :editor="store.editor"
        :language="appStore.locale"
      />
      <RTEControlCustomSelect
        v-if="staticCustomValues"
        :editor="store.editor"
        :custom-menus="customMenuOptions"
      >
        <template #trigger="slotProps">
          <CustomSelectTrigger
            :title="slotProps.title"
            :icon="slotProps.icon"
          />
        </template>
      </RTEControlCustomSelect>
    </template>
  </div>
</template>

<style lang="scss">
#toolbar_left {
  align-items: center;
  height: 100%;
  width: 10rem;
  justify-content: space-evenly;
  border-right: 1px solid #eaecf0;
}
#toolbar_select--text_block {
  min-width: 120px;
}
#toolbar_select--font_family {
  min-width: 145px;
}
.n-popover.n-popover-shared.n-popover--raw.n-popover-shared--show-arrow {
  .n-popover__content {
    background-color: #fff;
  }
}
.editor__line-height {
  min-width: 72px !important;
}

.font-dropdown-container {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
  width: auto;
  min-width: 200px;
  max-height: 320px;
}

.font-dropdown-container ul {
  height: auto !important;
  max-height: 250px !important;
  overflow-y: auto !important;
}

.font-dropdown-container .h-32 {
  height: auto !important;
  overflow: visible !important;
}
</style>
